import type { QueryClient } from '@tanstack/react-query'
import { BANKING_KEYS, CASH_POSITION_SUMMARIES_KEY } from '@/lib/query-keys'

/**
 * Invalidate every cache a manual transaction write — create, edit or delete —
 * can change. Shared by `useCreateManualTransactions`,
 * `useUpdateManualTransaction` and `useDeleteManualTransaction`.
 *
 * - the Manual Transactions list and its counts, and the KPI tiles;
 * - LOC Usage / Remaining Credit Availability and the LOC drill-down, derived
 *   from 'LINE OF CREDIT' transactions (`get_loc_availability` /
 *   `get_loc_transactions`) — a write may add, remove or reclassify one;
 * - Cash Position Summary, which reads LOC Usage too;
 * - a credit card's "View transactions" list, as the row may sit on a card,
 *   and Credit Card Availability, whose `has_transactions` decides whether
 *   the card offers "View transactions" at all.
 *
 * @param queryClient - The active query client
 * @param organizationId - The current organization, for the per-org LOC keys
 */
export function invalidateAfterManualTransactionWrite(
  queryClient: QueryClient,
  organizationId: string | undefined,
): void {
  void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allManualTransactions })
  void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allManualTransactionCounts })
  void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allKpi })
  void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.locAvailability(organizationId) })
  void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.locTransactions(organizationId) })
  void queryClient.invalidateQueries({ queryKey: CASH_POSITION_SUMMARIES_KEY })
  void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allCreditCardTransactions })
  void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allCreditCardAvailability })
}
